/**
 * Phase 89E — Outbound Webhook Dispatcher console.
 *
 * Super-Admin-only surface for managing HMAC-signed outbound webhooks
 * (academy.subscription_changed, fee.paid) to external CRMs.
 *
 * Wires to:
 *   GET    /api/super-admin/webhooks/endpoints
 *   POST   /api/super-admin/webhooks/endpoints
 *   PATCH  /api/super-admin/webhooks/endpoints/{id}
 *   DELETE /api/super-admin/webhooks/endpoints/{id}
 *   GET    /api/super-admin/webhooks/deliveries
 *   POST   /api/super-admin/webhooks/deliveries/{id}/replay
 *   POST   /api/super-admin/webhooks/deliveries/replay-failed
 */
import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Webhook, Plus, RefreshCw, RotateCcw, Trash2, Activity,
  CheckCircle2, AlertTriangle, Clock, Loader2, ChevronDown, ChevronRight,
  Copy, Power, KeyRound,
} from 'lucide-react';
import { toast } from 'sonner';

const ALLOWED_EVENTS = ['fee.paid', 'academy.subscription_changed'];

const fmtTs = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      dateStyle: 'medium', timeStyle: 'short',
    });
  } catch { return iso; }
};

const StatusBadge = ({ status }) => {
  if (status === 'success') return <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white text-[10px]"><CheckCircle2 className="h-3 w-3 mr-1" /> success</Badge>;
  if (status === 'pending') return <Badge variant="outline" className="border-amber-400 text-amber-700 dark:text-amber-300 text-[10px]"><Clock className="h-3 w-3 mr-1" /> pending</Badge>;
  if (status === 'dead_letter') return <Badge variant="outline" className="border-rose-400 text-rose-700 dark:text-rose-300 text-[10px]"><AlertTriangle className="h-3 w-3 mr-1" /> dead-letter</Badge>;
  return <Badge variant="outline" className="text-[10px]">{status}</Badge>;
};

const EndpointStatusPill = ({ status }) => {
  if (status === 'success') return <Badge className="bg-emerald-600 text-white text-[10px]"><CheckCircle2 className="h-3 w-3 mr-1" /> healthy</Badge>;
  if (status === 'failing') return <Badge variant="outline" className="border-amber-400 text-amber-700 dark:text-amber-300 text-[10px]"><Clock className="h-3 w-3 mr-1" /> retrying</Badge>;
  if (status === 'dead_letter') return <Badge variant="outline" className="border-rose-400 text-rose-700 dark:text-rose-300 text-[10px]"><AlertTriangle className="h-3 w-3 mr-1" /> failing</Badge>;
  return <Badge variant="outline" className="text-[10px]"><Clock className="h-3 w-3 mr-1" /> awaiting first delivery</Badge>;
};

function CreateEndpointForm({ onCreated }) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [events, setEvents] = useState([]);  // empty = all
  const [saving, setSaving] = useState(false);

  const toggleEvent = (e) =>
    setEvents(events.includes(e) ? events.filter(x => x !== e) : [...events, e]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !url.trim() || !secret.trim()) {
      toast.error('Name, URL and secret are all required');
      return;
    }
    if (secret.length < 12) {
      toast.error('Secret must be at least 12 chars');
      return;
    }
    setSaving(true);
    try {
      await api.post('/super-admin/webhooks/endpoints', {
        name: name.trim(),
        url: url.trim(),
        secret: secret.trim(),
        events,
      });
      toast.success('Endpoint added');
      setName(''); setUrl(''); setSecret(''); setEvents([]);
      onCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create endpoint');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="create-endpoint-form">
      <div className="space-y-1.5">
        <Label className="text-xs">Display Name</Label>
        <Input data-testid="endpoint-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. HubSpot CRM" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Destination URL</Label>
        <Input data-testid="endpoint-url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://crm.example.com/webhooks/outplay" />
      </div>
      <div className="space-y-1.5 md:col-span-2">
        <Label className="text-xs">HMAC Secret <span className="text-[10px] text-muted-foreground">(min 12 chars · encrypted at rest)</span></Label>
        <Input
          data-testid="endpoint-secret-input"
          type="password"
          value={secret}
          onChange={(e) => setSecret(e.target.value)}
          placeholder="••••••••••••"
        />
      </div>
      <div className="space-y-1.5 md:col-span-2">
        <Label className="text-xs">Subscribe to events <span className="text-[10px] text-muted-foreground">(leave none selected to subscribe to ALL allowed events)</span></Label>
        <div className="flex flex-wrap gap-2">
          {ALLOWED_EVENTS.map(e => (
            <button
              type="button"
              key={e}
              data-testid={`event-toggle-${e}`}
              onClick={() => toggleEvent(e)}
              className={`text-[11px] font-mono px-2.5 py-1 rounded-md border transition-colors ${
                events.includes(e)
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'border-border hover:bg-accent'
              }`}
            >
              {e}
            </button>
          ))}
        </div>
      </div>
      <div className="md:col-span-2 flex justify-end">
        <Button type="submit" disabled={saving} data-testid="create-endpoint-submit" className="bg-indigo-600 hover:bg-indigo-700 text-white">
          {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
          Add Endpoint
        </Button>
      </div>
    </form>
  );
}

function EndpointRow({ ep, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [showRotate, setShowRotate] = useState(false);
  const [newSecret, setNewSecret] = useState('');

  const toggleActive = async () => {
    setBusy(true);
    try {
      await api.patch(`/super-admin/webhooks/endpoints/${ep.id}`, { active: !ep.active });
      toast.success(ep.active ? 'Endpoint disabled' : 'Endpoint enabled');
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  const rotateSecret = async () => {
    if (newSecret.length < 12) {
      toast.error('Secret must be at least 12 chars');
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/super-admin/webhooks/endpoints/${ep.id}`, { secret: newSecret });
      toast.success('Secret rotated');
      setNewSecret('');
      setShowRotate(false);
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rotate failed');
    } finally {
      setBusy(false);
    }
  };

  const deleteEndpoint = async () => {
    if (!window.confirm(`Delete endpoint "${ep.name}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/super-admin/webhooks/endpoints/${ep.id}`);
      toast.success('Endpoint deleted');
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border rounded-xl p-4 space-y-3" data-testid={`endpoint-row-${ep.id}`}>
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-sm">{ep.name}</p>
            <EndpointStatusPill status={ep.last_delivery_status} />
            {!ep.active && <Badge variant="outline" className="text-[10px]">disabled</Badge>}
          </div>
          <p className="text-xs font-mono text-muted-foreground mt-1 truncate" title={ep.url}>{ep.url}</p>
          <div className="flex items-center gap-2 flex-wrap mt-1.5">
            <span className="text-[10px] text-muted-foreground">Events:</span>
            {(ep.events && ep.events.length > 0)
              ? ep.events.map(e => <Badge key={e} variant="outline" className="text-[10px] font-mono">{e}</Badge>)
              : <Badge variant="outline" className="text-[10px]">all allowed</Badge>}
          </div>
          <p className="text-[10px] text-muted-foreground mt-1.5">
            Created {fmtTs(ep.created_at)} by {ep.created_by || '—'}
            {ep.last_delivery_at && <> · Last delivery {fmtTs(ep.last_delivery_at)}</>}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            size="sm"
            variant="outline"
            onClick={toggleActive}
            disabled={busy}
            data-testid={`endpoint-toggle-${ep.id}`}
            title={ep.active ? 'Disable endpoint' : 'Enable endpoint'}
          >
            <Power className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowRotate(v => !v)}
            disabled={busy}
            data-testid={`endpoint-rotate-${ep.id}`}
            title="Rotate secret"
          >
            <KeyRound className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={deleteEndpoint}
            disabled={busy}
            data-testid={`endpoint-delete-${ep.id}`}
            className="text-rose-600 hover:text-rose-700"
            title="Delete endpoint"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      {showRotate && (
        <div className="flex items-end gap-2 pt-2 border-t" data-testid={`rotate-secret-row-${ep.id}`}>
          <div className="flex-1">
            <Label className="text-xs">New HMAC secret (min 12 chars)</Label>
            <Input
              type="password"
              value={newSecret}
              onChange={(e) => setNewSecret(e.target.value)}
              placeholder="••••••••••••"
              data-testid={`new-secret-input-${ep.id}`}
            />
          </div>
          <Button onClick={rotateSecret} disabled={busy} size="sm" data-testid={`save-rotated-secret-${ep.id}`}>
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <KeyRound className="h-3.5 w-3.5 mr-1" />}
            Save
          </Button>
        </div>
      )}
    </div>
  );
}

function DeliveryRow({ d, onReplayed }) {
  const [expanded, setExpanded] = useState(false);
  const [replaying, setReplaying] = useState(false);

  const replay = async (e) => {
    e.stopPropagation();
    setReplaying(true);
    try {
      const r = await api.post(`/super-admin/webhooks/deliveries/${d.id}/replay`);
      const status = r.data?.delivery?.status;
      if (status === 'success') toast.success('Replay succeeded');
      else if (status === 'pending') toast('Replay attempted — pending retry');
      else toast.error(`Replay outcome: ${status}`);
      onReplayed?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Replay failed');
    } finally {
      setReplaying(false);
    }
  };

  const lastAttempt = (d.attempts || [])[((d.attempts || []).length - 1)];
  return (
    <>
      <tr
        onClick={() => setExpanded(v => !v)}
        className="border-b border-border/60 hover:bg-muted/40 cursor-pointer"
        data-testid={`delivery-row-${d.id}`}
      >
        <td className="py-2 px-2">
          {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </td>
        <td className="py-2 px-2 font-mono text-[10px]">{fmtTs(d.created_at)}</td>
        <td className="py-2 px-2 font-mono text-[11px]">{d.event_type}</td>
        <td className="py-2 px-2 text-xs truncate max-w-[260px]" title={d.endpoint_url}>{d.endpoint_url}</td>
        <td className="py-2 px-2"><StatusBadge status={d.status} /></td>
        <td className="py-2 px-2 text-center text-xs font-mono">{(d.attempts || []).length}</td>
        <td className="py-2 px-2 text-xs font-mono">{lastAttempt?.http_status ?? (lastAttempt?.error ? 'ERR' : '—')}</td>
        <td className="py-2 px-2 text-right">
          <Button
            size="sm"
            variant="outline"
            onClick={replay}
            disabled={replaying}
            data-testid={`delivery-replay-${d.id}`}
            className="h-7 text-xs"
          >
            {replaying ? <Loader2 className="h-3 w-3 animate-spin" /> : <><RotateCcw className="h-3 w-3 mr-1" /> Replay</>}
          </Button>
        </td>
      </tr>
      {expanded && (
        <tr data-testid={`delivery-expanded-${d.id}`}>
          <td colSpan={8} className="bg-muted/30 px-4 py-3 border-b">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
              <div>
                <p className="font-semibold mb-1">Request Payload</p>
                <pre className="bg-white dark:bg-slate-900 border rounded p-2 text-[10px] font-mono overflow-x-auto max-h-40">
                  {JSON.stringify(d.payload, null, 2)}
                </pre>
                <p className="text-[10px] mt-1.5 text-muted-foreground">
                  Idempotency key: <span className="font-mono">{d.idempotency_key}</span>
                </p>
              </div>
              <div>
                <p className="font-semibold mb-1">Attempts ({(d.attempts || []).length})</p>
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {(d.attempts || []).map((a) => (
                    <div key={a.n} className="bg-white dark:bg-slate-900 border rounded p-2 text-[10px]">
                      <div className="flex items-center justify-between">
                        <span>Attempt #{a.n} · {fmtTs(a.attempted_at)}</span>
                        <span className={`font-mono ${
                          a.http_status >= 200 && a.http_status < 300
                            ? 'text-emerald-600'
                            : a.http_status ? 'text-rose-600' : 'text-amber-600'
                        }`}>
                          {a.http_status ?? a.error ?? '—'} · {a.latency_ms}ms
                        </span>
                      </div>
                      {a.response_body && (
                        <pre className="mt-1 text-[9px] font-mono overflow-x-auto max-h-16 text-muted-foreground">
                          {a.response_body.slice(0, 300)}
                        </pre>
                      )}
                    </div>
                  ))}
                  {(d.attempts || []).length === 0 && (
                    <p className="text-[10px] text-muted-foreground italic">No attempts yet — awaiting first delivery</p>
                  )}
                </div>
                {d.dead_letter_reason && (
                  <p className="mt-2 text-[10px] text-rose-700 dark:text-rose-300">
                    Dead-letter reason: <span className="font-mono">{d.dead_letter_reason}</span>
                  </p>
                )}
                {d.replayed_at && (
                  <p className="mt-1 text-[10px] text-muted-foreground">
                    Last replayed: {fmtTs(d.replayed_at)} by {d.replayed_by || '—'}
                  </p>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function SuperAdminWebhooks() {
  const [endpoints, setEndpoints] = useState(null);
  const [deliveries, setDeliveries] = useState(null);
  const [failedIn24h, setFailedIn24h] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [eventFilter, setEventFilter] = useState('all');
  const [endpointFilter, setEndpointFilter] = useState('all');
  const [replayingAll, setReplayingAll] = useState(false);

  const loadEndpoints = useCallback(async () => {
    try {
      const r = await api.get('/super-admin/webhooks/endpoints');
      setEndpoints(r.data.endpoints || []);
    } catch (err) {
      toast.error('Failed to load endpoints');
      setEndpoints([]);
    }
  }, []);

  const loadDeliveries = useCallback(async () => {
    try {
      const qs = new URLSearchParams();
      if (statusFilter !== 'all') qs.set('status', statusFilter);
      if (eventFilter !== 'all') qs.set('event_type', eventFilter);
      if (endpointFilter !== 'all') qs.set('endpoint_id', endpointFilter);
      qs.set('limit', '100');
      const r = await api.get(`/super-admin/webhooks/deliveries?${qs.toString()}`);
      setDeliveries(r.data.deliveries || []);
      setFailedIn24h(r.data.failed_in_24h || 0);
    } catch (err) {
      toast.error('Failed to load deliveries');
      setDeliveries([]);
    }
  }, [statusFilter, eventFilter, endpointFilter]);

  useEffect(() => { loadEndpoints(); }, [loadEndpoints]);
  useEffect(() => { loadDeliveries(); }, [loadDeliveries]);

  const replayAllFailed = async () => {
    if (!window.confirm(`Replay all ${failedIn24h} dead-letter deliveries from the last 24h?`)) return;
    setReplayingAll(true);
    try {
      const r = await api.post('/super-admin/webhooks/deliveries/replay-failed');
      toast.success(`Replayed ${r.data?.replayed ?? 0} deliveries`);
      loadDeliveries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk replay failed');
    } finally {
      setReplayingAll(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-display font-semibold flex items-center gap-2">
          <Webhook className="h-6 w-6 text-indigo-600" />
          Outbound Webhooks
        </h1>
        <p className="text-sm text-muted-foreground">
          HMAC-signed outbound webhooks to external CRMs. Currently fires on{' '}
          <span className="font-mono">academy.subscription_changed</span> and{' '}
          <span className="font-mono">fee.paid</span>.
        </p>
      </div>

      {/* Endpoints management */}
      <Card data-testid="endpoints-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-sm">Configured endpoints</CardTitle>
            <Button size="sm" variant="outline" onClick={loadEndpoints} data-testid="refresh-endpoints">
              <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <CreateEndpointForm onCreated={loadEndpoints} />
          {endpoints === null ? (
            <Skeleton className="h-20 w-full" />
          ) : endpoints.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4" data-testid="endpoints-empty">
              No endpoints configured yet. Add one above to start receiving HMAC-signed events.
            </p>
          ) : (
            <div className="space-y-3">
              {endpoints.map(ep => <EndpointRow key={ep.id} ep={ep} onChanged={loadEndpoints} />)}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Deliveries list */}
      <Card data-testid="deliveries-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Activity className="h-4 w-4 text-indigo-600" /> Recent deliveries
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              {failedIn24h > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={replayAllFailed}
                  disabled={replayingAll}
                  data-testid="replay-all-failed-btn"
                  className="border-rose-300 text-rose-700 hover:bg-rose-50"
                >
                  {replayingAll ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5 mr-1" />}
                  Replay all failed in 24h ({failedIn24h})
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={loadDeliveries} data-testid="refresh-deliveries">
                <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-7 text-xs w-[140px]" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="success">success</SelectItem>
                <SelectItem value="pending">pending</SelectItem>
                <SelectItem value="dead_letter">dead-letter</SelectItem>
              </SelectContent>
            </Select>
            <Select value={eventFilter} onValueChange={setEventFilter}>
              <SelectTrigger className="h-7 text-xs w-[220px]" data-testid="filter-event"><SelectValue placeholder="Event" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All events</SelectItem>
                {ALLOWED_EVENTS.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
              </SelectContent>
            </Select>
            {endpoints && endpoints.length > 0 && (
              <Select value={endpointFilter} onValueChange={setEndpointFilter}>
                <SelectTrigger className="h-7 text-xs w-[200px]" data-testid="filter-endpoint"><SelectValue placeholder="Endpoint" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All endpoints</SelectItem>
                  {endpoints.map(ep => <SelectItem key={ep.id} value={ep.id}>{ep.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {deliveries === null ? (
            <div className="p-4"><Skeleton className="h-20 w-full" /></div>
          ) : deliveries.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-8" data-testid="deliveries-empty">
              No deliveries match these filters yet.
            </p>
          ) : (
            <table className="w-full text-left text-xs" data-testid="deliveries-table">
              <thead className="text-[10px] uppercase tracking-wider text-muted-foreground border-b">
                <tr>
                  <th className="py-2 px-2 w-6"></th>
                  <th className="py-2 px-2">When</th>
                  <th className="py-2 px-2">Event</th>
                  <th className="py-2 px-2">Endpoint URL</th>
                  <th className="py-2 px-2">Status</th>
                  <th className="py-2 px-2 text-center">#</th>
                  <th className="py-2 px-2">Last code</th>
                  <th className="py-2 px-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {deliveries.map(d => <DeliveryRow key={d.id} d={d} onReplayed={loadDeliveries} />)}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
